"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import Typography from "@mui/material/Typography";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  createBusDriver,
  updateBusDriver,
  deleteBusDriver,
  createBulkBusDriver,
  deleteAllBusDriver,
} from "../../api/busDrivers";
import customFetch from "../../hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";

const DriversTab = () => {
  const [rows, setRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    DriverID: null,
    DriverName: "",
    EmployeeCode: "",
    ContactNumber: "",
    Email: "",
    Status: "Active",
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canConfirm, setCanConfirm] = useState(false);
  const countdownRef = useRef(null);

  // Excel menu state
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (openDeleteAllDialog && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanConfirm(true);
      clearTimeout(countdownRef.current);
    }

    return () => clearTimeout(countdownRef.current);
  }, [openDeleteAllDialog, countdown]);

  const handleCloseDeleteAll = () => {
    setOpenDeleteAllDialog(false);
    setCountdown(5);
    setCanConfirm(false);
    clearTimeout(countdownRef.current);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      const response = await deleteAllBusDriver();
      if (response.status) {
        setRows([]); // Clear the table immediately
        alert("All drivers deleted successfully!");
      } else {
        alert("Failed to delete all drivers: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Delete all drivers failed:", error);
      alert("Operation failed");
    } finally {
      handleCloseDeleteAll();
    }
  };

  const fetchDrivers = async () => {
    try {
      const data = await customFetch("getDriverDetails/0");
      setRows(data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  // Optimistic update helpers
  const updateLocalRows = (updatedDriver) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.DriverID === updatedDriver.DriverID ? { ...row, ...updatedDriver } : row
      )
    );
  };

  const addLocalRow = (newDriver) => {
    setRows((prevRows) => [...prevRows, newDriver]);
  };

  const removeLocalRow = (id) => {
    setRows((prevRows) => prevRows.filter((row) => row.DriverID !== id));
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({
      DriverID: null,
      DriverName: "",
      EmployeeCode: "",
      ContactNumber: "",
      Email: "",
      Status: "Active",
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (driver) => {
    setIsEditMode(true);
    setFormData({
      DriverID: driver.DriverID,
      DriverName: driver.DriverName || "",
      EmployeeCode: driver.EmployeeCode || "",
      ContactNumber: driver.ContactNumber || "",
      Email: driver.Email || "",
      Status: driver.Status || "Active",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      DriverID: null,
      DriverName: "",
      EmployeeCode: "",
      ContactNumber: "",
      Email: "",
      Status: "Active",
    });
  };

  const handleSubmit = async () => {
    if (!formData.DriverName.trim() || !formData.EmployeeCode.trim()) {
      alert("Driver Name and Employee Code are required");
      return;
    }

    const payload = {
      DriverName: formData.DriverName,
      EmployeeCode: formData.EmployeeCode,
      ContactNumber: formData.ContactNumber || "",
      Email: formData.Email || "",
      Status: formData.Status,
    };

    try {
      let response;
      if (isEditMode) {
        payload.DriverID = formData.DriverID;
        response = await updateBusDriver(payload);
        if (response.status) {
          updateLocalRows({ ...payload, DriverID: formData.DriverID });
          alert("Driver updated successfully!");
          handleCloseModal();
        }
      } else {
        response = await createBusDriver(payload);
        if (response.status && response.data && response.data.length > 0) {
          const createdDriver = response.data[0];
          addLocalRow(createdDriver);
          alert("Driver added successfully!");
          handleCloseModal();
        } else {
          // Fallback: refetch if no data returned
          await fetchDrivers();
          alert("Driver added successfully!");
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error("Error saving driver:", error);
      alert("Failed to save driver");
    }
  };

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBusDriver(deleteId);
      removeLocalRow(deleteId);
      alert("Driver deleted successfully!");
    } catch (error) {
      console.error("Error deleting driver:", error);
      alert("Failed to delete driver");
    }
    setOpenConfirmDialog(false);
  };

  // Excel handlers
  const handleExcelButtonClick = (event) => {
    setExcelMenuAnchorEl(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchorEl(null);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [["DriverName", "EmployeeCode", "ContactNumber", "Email", "Status"]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Drivers");
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fileName = `drivers_template_${dateStr}_${timeStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
    handleExcelMenuClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
    handleExcelMenuClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        await bulkCreateDrivers(data);
      }
    } catch (error) {
      console.error("Error processing Excel file:", error);
    }
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validData = jsonData.filter((item) => {
            const isValid =
              item.DriverName &&
              typeof item.DriverName === "string" &&
              item.EmployeeCode &&
              (typeof item.EmployeeCode === "string" || typeof item.EmployeeCode === "number");

            if (isValid) {
              item.EmployeeCode = String(item.EmployeeCode);
              item.ContactNumber = item.ContactNumber ? String(item.ContactNumber) : "";
              item.Email = item.Email || "";
              item.Status = item.Status || "Active";
            }
            return isValid;
          });
          resolve(validData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const bulkCreateDrivers = async (driversData) => {
    try {
      const response = await createBulkBusDriver(driversData);
      if (response.status) {
        fetchDrivers(); // Full refresh after bulk import
      }
    } catch (error) {
      console.error("Error bulk creating drivers:", error);
    }
  };

  const columns = [
    // { field: "DriverID", headerName: "Driver ID", width: 100 },
    { field: "DriverName", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "EmployeeCode", headerName: "Employee Code", flex: 1, minWidth: 150 },
    { field: "ContactNumber", headerName: "Contact Number", flex: 1, minWidth: 150 },
    { field: "Email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "Status", headerName: "Status", flex: 1, minWidth: 100 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeleteAllDialog(true);
            setCountdown(5);
            setCanConfirm(false);
          }}
        >
          Delete All Drivers
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExcelButtonClick}
          startIcon={<BackupTableIcon />}
        >
          Excel
        </Button>
        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={handleExcelMenuClose}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Driver
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.DriverID}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Modal for Add/Edit */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Driver" : "Add New Driver"}</h2>

          <TextField
            autoFocus
            margin="dense"
            label="Driver Name"
            fullWidth
            value={formData.DriverName}
            onChange={(e) => setFormData({ ...formData, DriverName: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Employee Code"
            fullWidth
            value={formData.EmployeeCode}
            onChange={(e) => setFormData({ ...formData, EmployeeCode: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Contact Number"
            fullWidth
            value={formData.ContactNumber}
            onChange={(e) => setFormData({ ...formData, ContactNumber: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={formData.Email}
            onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={formData.Status}
              label="Status"
              onChange={(e) => setFormData({ ...formData, Status: e.target.value })}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openDeleteAllDialog} onClose={handleCloseDeleteAll}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Delete ALL Drivers?
          </Typography>
          <Typography variant="body1" sx={{ my: 3 }}>
            This will permanently delete all drivers and cannot be undone.
          </Typography>
          <Typography variant="h4" sx={{ my: 4, fontWeight: "bold" }}>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canConfirm
              ? "You can now confirm deletion"
              : "Please wait for the countdown to finish"}
          </Typography>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button onClick={handleCloseDeleteAll} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAll}
              disabled={!canConfirm}
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this driver? This action cannot be undone."
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default DriversTab;
